import React, { useState, useEffect } from 'react';
import {
  DollarSign, MapPin, Info, AlertTriangle, ChevronDown, ChevronUp,
  CreditCard, Coins
} from 'lucide-react';
import { TollDetails, VehicleType, PaymentMethod, Currency } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import useTranslate from '../hooks/useTranslate';
import { formatCurrency, convertCurrency, getCurrencySymbol, getTranslatedCurrencyName } from '../utils/currencyUtils';
import { calculateCostWithTax } from '../utils/calculationUtils';

interface TollDetailsPanelProps {
  tollDetails: TollDetails | null;
  vehicleType: VehicleType | undefined;
  taxPercentage: number;
  onTaxPercentageChange?: (percentage: number) => void;
  selectedCurrency: Currency;
  onCurrencyChange?: (currency: Currency) => void;
  tollErrorOccurred: boolean;
  adjustedTotalTollCost: number;
  isMultiSegmentRoute: boolean;
}

const TollDetailsPanel: React.FC<TollDetailsPanelProps> = ({
  tollDetails,
  vehicleType,
  taxPercentage,
  onTaxPercentageChange,
  selectedCurrency,
  onCurrencyChange,
  tollErrorOccurred,
  adjustedTotalTollCost,
  isMultiSegmentRoute
}) => {
  const { t } = useLanguage();
  const { translatePaymentMethod } = useTranslate();
  const [showDetails, setShowDetails] = useState(false);

  // Calcular los costos totales convertidos
  const convertedTotalTollCost = convertCurrency(adjustedTotalTollCost, tollDetails?.tollCurrency || Currency.MXN, selectedCurrency);
  const totalTollCostWithoutTax = calculateCostWithTax(adjustedTotalTollCost, taxPercentage);
  const convertedTotalTollCostWithoutTax = convertCurrency(totalTollCostWithoutTax, tollDetails?.tollCurrency || Currency.MXN, selectedCurrency);

  const getPaymentMethodIcon = (method: PaymentMethod) => {
    switch (method) {
      case PaymentMethod.CASH:
        return <Coins size={14} className="text-green-600" />;
      case PaymentMethod.CARD:
        return <CreditCard size={14} className="text-blue-600" />;
      case PaymentMethod.TAG:
      case PaymentMethod.IAVE:
        return <CreditCard size={14} className="text-purple-600" />; // Using CreditCard for TAG/IAVE as a generic electronic payment
      default:
        return null;
    }
  };

  if (!tollDetails && !tollErrorOccurred) {
    return null; // Don't render if no toll data and no error
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-6">
      <div
        className="p-4 bg-primary text-white flex justify-between items-center cursor-pointer"
        onClick={() => setShowDetails(!showDetails)}
      >
        <h2 className="text-lg font-bold flex items-center">
          <DollarSign className="mr-2" size={20} />
          {t('tollDetails.title')}
        </h2>
        <button className="p-1 rounded-full hover:bg-primary-dark transition-colors">
          {showDetails ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {showDetails && (
        <div className="p-4 sm:p-6">
          {tollErrorOccurred && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-amber-800 font-medium mb-1">
                    {t('quotationForm.errors.tollApiError')}
                  </h4>
                  <p className="text-amber-700 text-sm">
                    {t('quotationForm.errors.costsMayNotIncludeTolls')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {tollDetails && tollDetails.tollStops.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">{t('tollDetails.totalTollbooths')}</h4>
                  <p className="text-2xl font-bold text-primary">{tollDetails.tollStops.length}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">{t('tollDetails.totalCost')}</h4>
                  <p className="text-2xl font-bold text-primary">
                    {getCurrencySymbol(selectedCurrency)}
                    {Math.ceil(convertedTotalTollCost).toLocaleString('es-MX')}
                    <span className="ml-2 text-base font-medium text-gray-600">
                      ({getTranslatedCurrencyName(selectedCurrency)})
                    </span>
                  </p>
                </div>
              </div>

              {/* Tax/VAT section */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                  <CreditCard size={16} className="mr-2" />
                  {t('tollDetails.vatTitle')}
                  {tollDetails?.tollCurrency && (
                    <span className="ml-2 text-xs font-medium text-gray-600">({tollDetails.tollCurrency})</span>
                  )}
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-blue-700">{t('tollDetails.originalCost')}</span>
                  <span className="font-medium text-blue-900 text-right">
                    {getCurrencySymbol(selectedCurrency)}
                    {Math.ceil(convertedTotalTollCost).toLocaleString('es-MX')}
                    <span className="ml-1 text-xs text-gray-600">
                      ({getTranslatedCurrencyName(selectedCurrency)})
                    </span>
                  </span>

                  <span className="text-blue-700">{t('tollDetails.vatDiscount')} ({taxPercentage}%)</span>
                  <span className="font-medium text-blue-900 text-right">
                    -{getCurrencySymbol(selectedCurrency)}
                    {Math.ceil(convertedTotalTollCost - convertedTotalTollCostWithoutTax).toLocaleString('es-MX')}
                  </span>
                  
                  <span className="text-blue-700 font-bold">{t('tollDetails.finalCost')}</span>
                  <span className="font-bold text-blue-900 text-right">
                    {getCurrencySymbol(selectedCurrency)}
                    {Math.ceil(convertedTotalTollCostWithoutTax).toLocaleString('es-MX')}
                    <span className="ml-1 text-xs text-gray-600">
                      ({getTranslatedCurrencyName(selectedCurrency)})
                    </span>
                  </span>
                </div>
                {onTaxPercentageChange && (
                  <div className="mt-4">
                    <label htmlFor="tollTaxPercentage" className="block text-sm font-medium text-blue-700 mb-1">
                      {t('quotationForm.taxVat')} (%)
                    </label>
                    <select
                      id="tollTaxPercentage"
                      value={taxPercentage}
                      onChange={(e) => onTaxPercentageChange(parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary appearance-none bg-white"
                    >
                      <option value={0}>0% - {t('quotationForm.costItems.noVat')}</option>
                      <option value={8}>8% - {t('quotationForm.costItems.reducedRate')}</option>
                      <option value={16}>16% - {t('quotationForm.costItems.standardVat')}</option>
                      <option value={20}>20% - {t('quotationForm.costItems.specialRate')}</option>
                      <option value={25}>25% - {t('quotationForm.costItems.highRate')}</option>
                      <option value={30}>30% - {t('quotationForm.costItems.veryHighRate')}</option>
                      <option value={35}>35% - {t('quotationForm.costItems.extremeRate')}</option>
                      <option value={40}>40% - {t('quotationForm.costItems.maximumRate')}</option>
                      <option value={45}>45% - {t('quotationForm.costItems.exceptionalRate')}</option>
                      <option value={50}>50% - {t('quotationForm.costItems.limitRate')}</option>
                    </select>
                    <p className="text-xs text-blue-600 mt-1">
                      {t('quotationForm.taxDiscountHelp')}
                    </p>
                  </div>
                )}
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('tollDetails.tollTable.tollbooth')}</h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200 mb-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {t('tollDetails.tollTable.tollbooth')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {t('tollDetails.tollTable.tollbooth')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('tollDetails.tollTable.cost')}
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('tollDetails.tollTable.paymentMethods')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tollDetails.tollStops.map((stop, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{stop.name}</div>
                          <div className="text-xs text-gray-500">{stop.operatedBy}</div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                          {getCurrencySymbol(selectedCurrency)}
                          {Math.ceil(convertCurrency(stop.cost, tollDetails.tollCurrency || Currency.MXN, selectedCurrency)).toLocaleString('es-MX')}
                          <span className="ml-1 text-xs text-gray-600">
                            ({getTranslatedCurrencyName(selectedCurrency)})
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            {stop.paymentMethods.map((method, idx) => (
                              <span key={idx} className="flex items-center text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                {getPaymentMethodIcon(method)}
                                <span className="ml-1">{translatePaymentMethod(method)}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-sm text-gray-600 mb-2">
                <Info size={16} className="inline-block mr-1.5 text-blue-500" />
                {t('tollDetails.tollNote')}
              </p>
              <p className="text-sm text-gray-600">
                <Info size={16} className="inline-block mr-1.5 text-blue-500" />
                {isMultiSegmentRoute ? t('tollDetails.onewayNote') : 'Nota: Los costos mostrados incluyen ida y vuelta.'}
              </p>
            </>
          ) : (
            <div className="p-4 text-center text-gray-500">
              <MapPin size={48} className="mx-auto mb-3" />
              <p>{t('quotationForm.errors.generateToSee')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TollDetailsPanel;